/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { ListSectionSpinner, PrimaryButton } from "../../components";
import { useNavigate } from "react-router";
import { useArticles } from "../../store/useArticles";
import { useEffect, useState, useMemo, useCallback } from "react";
import { ArticlesTable } from "./ArticlesTable";
import { useDropLocations } from "../../store/useDropLocations";
import DropdownFilter from "../../components/modals/DropdownFilter";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useAutoAnimate } from "@formkit/auto-animate/react";

dayjs.extend(relativeTime);

function ArticlesScreen() {
  const [animationParent] = useAutoAnimate();
  /** Hooks */
  const navigate = useNavigate();
  const {
    getArticles,
    articles,
    hasNextPage,
    articlesError,
    articlesLoading,
  } = useArticles();
  const { dropLocations, getDropLocations } = useDropLocations();
  /** State */
  const [page, setPage] = useState(1);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);

  const addArticle = () => navigate("/add-article");

  useEffect(() => {
    // Locations power the pin-target picker and the readable names in the table;
    // pinned articles arrive inline with the listing, so there's no extra fetch.
    getDropLocations();
  }, []);

  useEffect(() => {
    if (dropLocations?.length && selectedLocations.length === 0) {
      // Set all locations as selected by default
      setSelectedLocations([
        "All",
        ...(dropLocations?.map((location) => location?.cost_place as string) ??
          []),
      ]);
    }
  }, [dropLocations]);

  useEffect(() => {
    if (selectedLocations.length > 0) {
      const locationIds = getFilteredLocations();
      setPage(1);
      getArticles(1, 20, locationIds);
    }
  }, [selectedLocations]);

  useEffect(() => {
    if (selectedLocations.length > 0 && page > 1) {
      const locationIds = getFilteredLocations();
      getArticles(page, 20, locationIds);
    }
  }, [page]);

  /** Get location options for dropdown */
  const locationOptions = useMemo(() => {
    return [
      "All",
      ...(dropLocations?.map((location) => location?.cost_place as string) ??
        []),
    ];
  }, [dropLocations]);

  /** Handle location selection */
  const onSelectLocations = useCallback(
    (option: string) => {
      if (option === "All") {
        if (selectedLocations.includes("All")) {
          setSelectedLocations([]);
          return;
        }
        setSelectedLocations([
          "All",
          ...(dropLocations?.map(
            (location) => location?.cost_place as string
          ) ?? []),
        ]);
        return;
      }
      setSelectedLocations((prev) => {
        if (prev.includes(option)) {
          const filtered = prev.filter(
            (item) => item !== option && item !== "All"
          );
          return filtered;
        }
        const updatedSelections = [
          ...prev.filter((item) => item !== "All"),
          option,
        ];
        return updatedSelections;
      });
    },
    [dropLocations, selectedLocations]
  );

  /** Get filtered location IDs for API */
  const getFilteredLocations = useCallback(() => {
    if (selectedLocations.includes("All") || selectedLocations.length === 0) {
      return []; // Send empty array to get all locations
    }

    // Map cost_place names to location IDs
    const locationIds = selectedLocations
      .filter((locationName) => locationName !== "All") // Remove "All" if present
      .map((locationName) => {
        const location = dropLocations?.find(
          (loc) => loc.cost_place === locationName
        );
        return location?._id;
      })
      .filter(Boolean) as string[]; // Remove undefined values

    return locationIds;
  }, [selectedLocations, dropLocations]);

  /** Loading: dropLocations init, pending first articles request while list still empty */
  const showArticlesListLoading =
    dropLocations === null ||
    (dropLocations.length > 0 && selectedLocations.length === 0) ||
    (articlesLoading && articles.length === 0);

  return (
    <div ref={animationParent}>
      <div className="flex w-full justify-between items-center mb-6">
        <h1 className="font-satoshi text-2xl font-bold text-dark">Articles</h1>
        <div className="flex gap-3 items-center">
          <DropdownFilter
            label="Locations"
            options={locationOptions}
            selectedOptions={selectedLocations}
            onSelect={onSelectLocations}
            buttonClassname="h-10"
          />
          <PrimaryButton
            onClick={addArticle}
            label="Add new article"
            className="h-10"
          />
        </div>
      </div>
      <div className="w-full px-7 py-7 bg-white rounded-xl border-gray-border drop-shadow-none">
        {showArticlesListLoading ? (
          <ListSectionSpinner label="Loading articles" />
        ) : articlesError ? (
          <p className="text-center font-satoshi text-base text-red-500 py-12">
            {articlesError}
          </p>
        ) : !articles?.length ? (
          <p className="text-center font-satoshi text-base text-dark">
            There is no article created yet.
          </p>
        ) : (
          <ArticlesTable />
        )}
      </div>
      {hasNextPage ? (
        <div className="flex flex-row w-full items-center justify-end mt-3">
          <PrimaryButton
            onClick={() => {
              const locationIds = getFilteredLocations();
              setPage(page + 1);
              getArticles(page + 1, 20, locationIds);
            }}
            label="Load more"
            className="h-10"
          />
        </div>
      ) : null}
    </div>
  );
}

export default withProtectedRoute(ArticlesScreen);
